import { call, put, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { 
  placeOrderRequest, 
  placeOrderSuccess, 
  placeOrderFailure, 
  fetchPaymentStatusRequest,
  fetchPaymentStatusSuccess,
  fetchPaymentStatusFailure,
  createDeliveryQuoteRequest,
  createDeliveryQuoteSuccess,
  createDeliveryQuoteFailure,
  orderRequest,
  orderSuccess,
  orderFailure,
  OrderRequest, 
  OrderResponse,
  PaymentStatusRequest,
  PaymentStatusResponse,
  DeliveryQuoteRequest,
  DeliveryQuoteResponse,
  OrderRequestV2
} from '../slices/orderSlice';
import { request } from '../../api/request';
import ENDPOINTS from '../../api/endpoints';
import { shouldSkipApiCalls } from '../../utils/appConfig';

function* placeOrderSaga(action: PayloadAction<OrderRequest>) {
  // Skip API calls if in preview mode
  if (shouldSkipApiCalls()) {
    return;
  }

  try {
    const response: { data: OrderResponse } = yield call(request, {
      method: 'POST',
      url: ENDPOINTS.PLACE_ORDER_API_PATH,
      data: action.payload,
    });
    
    yield put(placeOrderSuccess(response.data));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to place order';
    yield put(placeOrderFailure(errorMessage));
  }
}

function* fetchPaymentStatusSaga(action: PayloadAction<PaymentStatusRequest>) {
  // Skip API calls if in preview mode
  if (shouldSkipApiCalls()) {
    return;
  }

  try {
    const response: { data: PaymentStatusResponse } = yield call(request, {
      method: 'GET',
      url: `/pos/order/track?order_id=${action.payload.order_id}`,
      headers: {
        'restaurantId': action.payload.restaurant_id,
      },
    });
    
    yield put(fetchPaymentStatusSuccess(response.data));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch payment status';
    yield put(fetchPaymentStatusFailure(errorMessage));
  }
}

function* createDeliveryQuoteSaga(action: PayloadAction<DeliveryQuoteRequest>) {
  // Skip API calls if in preview mode
  if (shouldSkipApiCalls()) {
    return;
  }

  try {
    const response: { data: DeliveryQuoteResponse } = yield call(request, {
      method: 'POST',
      url: ENDPOINTS.CREATE_DELIVERY_QUOTE_API_PATH,
      data: action.payload,
      headers: {
        'restaurantId': action.payload.restaurant_id,
      },
    });
    
    yield put(createDeliveryQuoteSuccess(response.data));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to create delivery quote';
    yield put(createDeliveryQuoteFailure(errorMessage));
  }
}

function* orderSaga(action: PayloadAction<OrderRequestV2>): Generator<any, void, any> {
  // Skip API calls if in preview mode
  if (shouldSkipApiCalls()) {
    return;
  }

  try {
    console.log('🚀 [Order Saga] Received complete payload from useOrder hook');
    console.log('📦 [Order Saga] Payload:', JSON.stringify(action.payload, null, 2));
    
    // Get restaurant ID from Redux state
    const restaurantId = '7cdb36d9-c51b-473e-9a9f-cfc468ee6d0b';
    
    console.log('🌐 [Order Saga] Sending request to:', ENDPOINTS.ORDER_API_PATH);
    console.log('🔑 [Order Saga] Request headers:', { 'restaurant-id': restaurantId });
    
    // Simple pass-through to API - payload is already complete from useOrder hook
    const response: { data: { data: any } } = yield call(request, {
      method: 'POST',
      url: `${ENDPOINTS.API_V2_BASE_URL}${ENDPOINTS.ORDER_API_PATH}`,
      data: action.payload,
      headers: {
        'restaurant-id': restaurantId,
      },
      requiresRestaurantId: false
    });
    
    console.log('✅ [Order Saga] Order successful! Full Response:', response.data);
    console.log('✅ [Order Saga] Order Data:', response.data.data);
    
    // Pass the actual order data (response.data.data), not the wrapper
    yield put(orderSuccess(response.data.data));
  } catch (error: any) {
    console.error('❌ [Order Saga] Order failed:', error);
    console.error('❌ [Order Saga] Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    
    const errorMessage = error.response?.data?.message || error.message || 'Failed to place order';
    yield put(orderFailure(errorMessage));
  }
}

export function* watchOrderSaga() {
  yield takeEvery(placeOrderRequest.type, placeOrderSaga);
  yield takeEvery(fetchPaymentStatusRequest.type, fetchPaymentStatusSaga);
  yield takeEvery(createDeliveryQuoteRequest.type, createDeliveryQuoteSaga);
  yield takeEvery(orderRequest.type, orderSaga);
}
